import { useCallback, useEffect, useState } from "react";

import { send } from "../api/auth";
import { expenses as api, type MyExpenses as Data } from "../api/expenses";
import { AddButton } from "../design/AddLink";
import { FileDrop } from "../design/FileDrop";
import { StatusChip } from "../design/StatusChip";
import type { Tone } from "../design/tone";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { money, whenIn } from "../system/format";
import { minorFrom } from "../system/payRules";

// ---------------------------------------------------------------------------
// Sweep §4 / 0190. A WORKER CLAIMS BACK MONEY SPENT ON ONE OF THEIR OWN SHIFTS.
//
// Worded through the translation layer (S64); the labels come worded from the API (S61).
// A mileage claim is a distance: the amount is the agency's rate times it,
// worked out by the database, so this screen never multiplies anything.
// ---------------------------------------------------------------------------

export function MyExpenses() {
  const { apiBase, me } = useSession();
  const { t, refusal, locale } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const [data, setData] = useState<Data | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await send<Partial<Data> & { error?: { message?: string } }>(api.mine(apiBase));
      if (res.status === 200 && Array.isArray(res.body.claims)) {
        setData(res.body as Data);
        setFailed(null);
      } else {
        setData(null);
        setFailed(refusal(res.body.error) ?? t("Your expense claims could not be read."));
      }
    } catch {
      setData(null);
      setFailed(t("We could not reach Verbunk. Check your connection and try again."));
    }
  }, [apiBase, t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="stack">
      <h1>{t("My expenses")}</h1>
      {failed ? (
        <div className="form-message" role="alert">
          <StatusChip tone="failed" label={t("Not available")} />
          <span>{failed}</span>
        </div>
      ) : null}
      {data ? (
        <section className="panel">
          <div className="panel-head">
            <h2>{t("What you have claimed")}</h2>
            {data.timesheets.length > 0 ? (
              <AddButton label={t("Claim an expense")} expanded={claiming} onClick={() => setClaiming(!claiming)} />
            ) : null}
          </div>
          {claiming ? (
            <ClaimForm
              data={data}
              orgZone={orgZone}
              onClaimed={async () => {
                setClaiming(false);
                await load();
              }}
              onCancel={() => setClaiming(false)}
            />
          ) : null}
          {data.timesheets.length === 0 ? (
            <p className="muted">{t("You can claim expenses once you have worked a shift and it has a timesheet.")}</p>
          ) : null}
          {data.claims.length === 0 ? <p className="muted">{t("You have not claimed any expenses.")}</p> : null}
          {data.claims.length > 0 ? (
            <ul className="plain-list">
              {data.claims.map((c) => (
                <li key={c.id} className="stack">
                  <div className="row">
                    <StatusChip tone={c.state_tone as Tone} label={translateLabel(locale, c.state_label)} />
                    <strong>{money(c.amount_minor, c.currency)}</strong>
                    <span>{translateLabel(locale, c.kind_label)}</span>
                  </div>
                  <span className="muted">
                    {c.event_name ?? translateLabel(locale, c.role_name)} · {t("shift {when}", { when: whenIn(c.shift_start, orgZone) })}
                    {/* S76: the unit's worded label, not its id ("12 mile"). The claim row
                        carries only the id, so the label is the agency's current unit's
                        when they match; a claim in an older unit keeps the id. */}
                    {c.distance !== null
                      ? ` · ${c.distance} ${translateLabel(locale, c.distance_unit_label ?? c.distance_unit ?? "")}`
                      : ""}
                    {c.has_receipt ? ` · ${t("receipt attached")}` : ""}
                    {c.rejection_reason_label ? ` · ${translateLabel(locale, c.rejection_reason_label)}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

function readFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function ClaimForm({ data, orgZone, onClaimed, onCancel }: { data: Data; orgZone: string | null; onClaimed: () => Promise<void>; onCancel: () => void }) {
  const { apiBase, me } = useSession();
  const { t, refusal, locale } = useT();
  const currency = me !== null && me.authenticated ? (me.active_org?.currency ?? "") : "";
  const [shift, setShift] = useState("");
  const [kind, setKind] = useState("");
  const [amount, setAmount] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const chosen = data.kinds.find((k) => k.id === kind);
  const mileage = chosen?.is_mileage === true;

  async function submit() {
    setNotice(null);
    let figure: Record<string, number>;
    if (mileage) {
      const distance = Number(amount.trim());
      if (!Number.isInteger(distance) || distance <= 0) {
        setNotice(t("Type the distance as a whole number."));
        return;
      }
      figure = { distance_travelled: distance };
    } else {
      const minor = minorFrom(amount, currency);
      if (minor === null || minor <= 0) {
        setNotice(t("Type the amount you spent, such as 2500."));
        return;
      }
      figure = { amount_minor: minor };
    }
    setBusy(true);
    try {
      const receipt = file
        ? { filename: file.name, content_type: file.type || "application/octet-stream", content_base64: await readFile(file) }
        : null;
      const res = await send<{ error?: { message?: string } }>(
        api.submit(apiBase, { timesheet_id: shift, kind, ...figure, receipt }),
      );
      if (res.status !== 201) {
        setNotice(refusal(res.body.error) ?? t("That claim could not be sent."));
        return;
      }
      setKind("");
      setAmount("");
      setFile(null);
      await onClaimed();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="field">
        <label htmlFor="expense-shift">{t("Which shift")}</label>
        <select id="expense-shift" required value={shift} onChange={(e) => setShift(e.target.value)}>
          <option value="">{t("Choose…")}</option>
          {data.timesheets.map((t) => (
            <option key={t.id} value={t.id}>
              {(t.event_name ?? t.role_name) + " · " + whenIn(t.shift_start, orgZone)}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="expense-kind">{t("What for")}</label>
        <select id="expense-kind" required value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">{t("Choose…")}</option>
          {data.kinds.map((k) => (
            <option key={k.id} value={k.id}>
              {translateLabel(locale, k.label)}
            </option>
          ))}
        </select>
        {chosen ? <p className="field-hint">{translateLabel(locale, chosen.description)}</p> : null}
      </div>
      <div className="field">
        <label htmlFor="expense-amount">
          {mileage
            ? t("Distance ({unit})", { unit: data.mileage?.unit_label ?? t("not available") })
            : t("Amount spent ({currency})", { currency })}
        </label>
        <input
          id="expense-amount"
          inputMode={mileage ? "numeric" : "decimal"}
          required
          disabled={mileage && data.mileage === null}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        {mileage && data.mileage ? (
          <p className="field-hint">
            {t("The agency pays {rate} per {unit}.", {
              rate: money(data.mileage.rate_minor, data.mileage.currency),
              unit: data.mileage.unit_label,
            })}
          </p>
        ) : null}
        {mileage && data.mileage === null ? (
          <p className="field-hint">{t("The agency has not set a mileage rate yet, so mileage cannot be claimed.")}</p>
        ) : null}
      </div>
      <div className="field">
        <label htmlFor="expense-receipt">{t("Receipt (optional, a photo or a PDF)")}</label>
        {/* The design system's drop zone, not the browser's file button (tour T-29). */}
        <FileDrop id="expense-receipt" accept="image/*,application/pdf" file={file} onFile={setFile} />
      </div>
      {notice ? (
        <div className="form-message" role="alert">
          <StatusChip tone="attention" label={t("Not sent")} />
          <span>{notice}</span>
        </div>
      ) : null}
      <div className="row">
        <button type="submit" className="btn" disabled={busy}>
          {busy ? t("Sending…") : t("Send the claim")}
        </button>
        <button type="button" className="btn btn-quiet" onClick={onCancel}>
          {t("Cancel")}
        </button>
      </div>
    </form>
  );
}
